class RentalDashboard {
    constructor() {
      // console.log('RentalDashboard: Starting initialization');
      const config = window.RENTAL_DASHBOARD_CONFIG;
      this.apiUrl = config?.apiUrl;
      this.customerEmail = config?.customerEmail;
      
      // Wait for DOM content to load
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init());
      } else {
        this.init();
      }
      this.bindEvents();
    }
  
    init() {
      if (this.customerEmail) {
        this.loadRentals();
      }
    }
  
    bindEvents() {
      document.addEventListener('click', (e) => {
        const rentalItem = e.target.closest('.rental-item');
        if (!rentalItem) return;
        
        const rentalId = rentalItem.dataset.rentalId;
        if (!rentalId) {
          console.error('No rental ID found');
          return;
        }
  
        if (e.target.classList.contains('go-to-order')) {
          this.showOrderDetails(rentalId);
        }
        
       
      });
    }
  
    async showOrderDetails(rentalId) {
      window.location.href = `/pages/rental-order?order_number=${rentalId}&email=${encodeURIComponent(this.customerEmail)}`;
    }
  
    async loadRentals() {
      const loadingState = document.getElementById('loading-state');
      const errorState = document.getElementById('error-state');
      const emptyState = document.getElementById('empty-state');
      const rentalsContainer = document.getElementById('rentals-container');
      
      if (!rentalsContainer) {
        console.error('RentalDashboard: Rentals container not found');
        return;
      }
      
      // console.log('RentalDashboard: Loading rentals for email:', this.customerEmail);
      // console.log('RentalDashboard: API URL:', `${this.apiUrl}/rentals/customer/public/?email=${encodeURIComponent(this.customerEmail)}`);
      
      try {
        if (loadingState) loadingState.classList.remove('hidden');
        if (errorState) errorState.classList.add('hidden');
        if (emptyState) emptyState.classList.add('hidden');
        
        const response = await fetch(
          `${this.apiUrl}/rentals/customer/public/?email=${encodeURIComponent(this.customerEmail)}`, 
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Origin': window.location.origin
            }
          }
        );
  
        // console.log('RentalDashboard: Response status:', response.status);
        const data = await response.json();
        // console.log('RentalDashboard: Response data:', data);
        
        if (!response.ok) {
          console.error('RentalDashboard: Request failed:', data.error || 'Failed to load rentals');
          throw new Error(data.error || 'Failed to load rentals');
        }
        
        if (data.rentals && data.rentals.length > 0) {
          // console.log('RentalDashboard: Found rentals:', data.rentals.length);
          
          // Update days left notification
          const activeRental = data.rentals.find(rental => {
            const endDate = new Date(rental.return_due_date);
            const today = new Date();
            return endDate > today && !rental.purchased;
          });
          
          if (activeRental) {
            const endDate = new Date(activeRental.return_due_date);
            const today = new Date();
            const daysLeft = Math.max(Math.ceil((endDate - today) / (1000 * 60 * 60 * 24)), 0);
            const daysLeftNotification = document.getElementById('days-left-notification');
            const daysLeftText = daysLeftNotification?.querySelector('.days-left-text');
            
            if (daysLeftText) {
              daysLeftText.textContent = `${daysLeft} Days Left`;
              daysLeftNotification.style.display = 'flex';
            }
          } else {
            const daysLeftNotification = document.getElementById('days-left-notification');
            if (daysLeftNotification) {
              daysLeftNotification.style.display = 'none';
            }
          }
          
          rentalsContainer.innerHTML = this.renderRentals(data.rentals);
          rentalsContainer.classList.remove('hidden');
          if (emptyState) emptyState.classList.add('hidden');
        } else {
          // console.log('RentalDashboard: No rentals found');
          if (rentalsContainer) rentalsContainer.classList.add('hidden');
          if (emptyState) emptyState.classList.remove('hidden');
        }
      } catch (error) {
        console.error('RentalDashboard: Error details:', error);
        if (errorState) {
          errorState.classList.remove('hidden');
          const errorMessage = errorState.querySelector('.error-message');
          if (errorMessage) {
            errorMessage.textContent = error.message || 'Failed to load rentals';
          }
        }
      } finally {
        if (loadingState) loadingState.classList.add('hidden');
      }
    }
  
    getOrderStatus(rental) {
      // console.log('getOrderStatus - Input rental:', rental);
      
      const statusMap = {
          // Payment Related Status
          'payment_settled': { text: 'Payment Settled', color: '#28a745', group: 'payment' },
          'emandate_pending': { text: 'E-Mandate Pending', color: '#ffc107', group: 'payment' },
          'emandate_failed': { text: 'E-Mandate Failed', color: '#dc3545', group: 'payment' },
          'initiated_first_payment': { text: 'Payment Initiated', color: '#17a2b8', group: 'payment' },
          'payment_failed': { text: 'Payment Failed', color: '#dc3545', group: 'payment' },
          'payment_confirmed': { text: 'Payment Confirmed', color: '#28a745', group: 'payment' },
          
          // KYC Related Status
          'kyc_pending': { text: 'KYC Pending', color: '#ffc107', group: 'kyc' },
          'kyc_rejected': { text: 'KYC Rejected', color: '#dc3545', group: 'kyc' },
          
          // Fulfillment Related Status
          'supplier_selection': { text: 'Supplier Selection', color: '#17a2b8', group: 'fulfillment' },
          'pending_fulfillment': { text: 'Pending Fulfillment', color: '#ffc107', group: 'fulfillment' },
          'shipped': { text: 'Shipped', color: '#17a2b8', group: 'fulfillment' },
          'delivered': { text: 'Rental Ongoing', color: '#28a745', group: 'fulfillment' },
          
          // Return Related Status
          'PENDING': { text: 'Return Requested', color: '#ffc107', group: 'return' },
          'RETURN_APPROVED': { text: 'Return Approved', color: '#28a745', group: 'return' },
          'PROCESSING_PICKUP': { text: 'Processing Pickup', color: '#17a2b8', group: 'return' },
          'RETURN_RECEIVED': { text: 'Return Received', color: '#28a745', group: 'return' },
          'SCHEDULE_PICKUP': { text: 'Return Scheduled', color: '#17a2b8', group: 'return' },
          'PICKED_UP': { text: 'Picked Up', color: '#28a745', group: 'return' },
          'QC_PENDING': { text: 'Return QC Pending', color: '#17a2b8', group: 'return' },
          'QC_COMPLETED': { text: 'Return QC Completed', color: '#28a745', group: 'return' },
          'REFUND_PENDING': { text: 'Return Refund Pending', color: '#ffc107', group: 'return' },
          'REFUND_PROCESSED': { text: 'Return Refund Processed', color: '#28a745', group: 'return' },
          'COMPLETED': { text: 'Return Completed', color: '#28a745', group: 'return' },
          'REJECTED': { text: 'Return Rejected', color: '#dc3545', group: 'return' },
          
          // Buyback/Purchase Related Status
          'purchase_pending': { text: 'Purchase In Progress', color: '#ffc107', group: 'purchase' },
          'purchase_completed': { text: 'Successfully Purchased', color: '#28a745', group: 'purchase' },
          'purchase_failed': { text: 'Purchase Failed', color: '#dc3545', group: 'purchase' },
          'purchased': { text: 'Product Purchased', color: '#28a745', group: 'purchase' },
          'PURCHASED': { text: 'Product Purchased', color: '#28a745', group: 'purchase' },
          'buyback_initiated': { text: 'Buyback Initiated', color: '#17a2b8', group: 'purchase' },
          'buyback_payment_pending': { text: 'Buyback Payment Pending', color: '#ffc107', group: 'purchase' },
          'buyback_completed': { text: 'Buyback Successful', color: '#28a745', group: 'purchase' },
          
          // Extension Related Status
          'extension_pending': { text: 'Extension Pending', color: '#ffc107', group: 'extension' },
          'extension_completed': { text: 'Extended', color: '#28a745', group: 'extension' }
      };
  
      const statuses = [];
      
      // Priority 1: Check for purchase status
      if (rental.status === 'PURCHASED' || rental.status === 'purchased') {
          return [{
              text: 'Product Purchased',
              color: '#28a745',
              group: 'purchase'
          }];
      }
      
      // Priority 2: Check return status
      if (rental.return_status && rental.return_status !== 'NOT_REQUESTED') {
          // console.log('Return status found:', rental.return_status);
          if (statusMap[rental.return_status]) {
              return [statusMap[rental.return_status]];
          }
      }
  
      // Check return details as fallback for return status
      if (rental.return_details && rental.return_details.length > 0) {
          // console.log('Return details found:', rental.return_details);
          const latestReturn = rental.return_details[rental.return_details.length - 1];
          if (latestReturn.status && statusMap[latestReturn.status]) {
              return [statusMap[latestReturn.status]];
          }
      }
      
      // Priority 3: Check main status
      if (rental.status) {
          const statusKey = rental.status.toLowerCase();
          if (statusMap[statusKey]) {
              statuses.push(statusMap[statusKey]);
          } else {
              statuses.push({
                  text: rental.display_status || rental.status,
                  color: '#6c757d',
                  group: 'other'
              });
          }
      }
  
      // console.log('Final statuses:', statuses);
      return statuses;
    }
  
    getStatusColor(status) {
      const colorMap = {
        'PAYMENT_COMPLETED': '#28a745',
        'KYC_PENDING': '#ffc107',
        'KYC_PROCESSING': '#17a2b8',
        'KYC_REJECTED': '#dc3545',
        'DELIVERED': '#28a745',
        'EXTENDED': '#28a745',
        'PENDING': '#ffc107'
      };
      return colorMap[status] || '#6c757d';
    }
  
    renderRentals(rentals) {
      // console.log('Rentals data received:', rentals);
  
      return rentals.map(rental => {
        const actions = rental.actions || {};
        const startDate = rental.delivery_timestamp ? new Date(rental.delivery_timestamp) : null;
        const endDate = new Date(rental.return_due_date);
        const today = new Date();
        
        const totalDays = rental.rental_period * 30;
        const elapsedDays = Math.ceil((today - startDate) / (1000 * 60 * 60 * 24));
        const progress = Math.min(Math.max((elapsedDays / totalDays) * 100, 0), 100);
        const daysLeft = Math.max(Math.ceil((endDate - today) / (1000 * 60 * 60 * 24)), 0);
  
        const statuses = this.getOrderStatus(rental);
        const isDelivered = Boolean(startDate);
        const showProgress = isDelivered && !statuses.some(s => s.text === 'Product Purchased');
  
        const statusHtml = `
          <div class="status-badges">
            ${statuses.map(status => `
              <span class="status-badge ${status.group}" 
                    style="background-color: ${status.color}20; color: ${status.color};"
                    title="${status.text}">
                ${status.text}
              </span>
            `).join('')}
          </div>
        `;
  
        // Only show timeline if delivered
        const showTimeline = rental.delivery_timestamp && 
                           !statuses.some(s => s.text === 'Product Purchased');
        
        if (showTimeline) {
          const totalDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
          const elapsedDays = Math.ceil((today - startDate) / (1000 * 60 * 60 * 24));
          const progress = Math.min(Math.max((elapsedDays / totalDays) * 100, 0), 100);
          const daysLeft = Math.max(Math.ceil((endDate - today) / (1000 * 60 * 60 * 24)), 0);
          
          // ... rest of the rendering code
        }
  
        return `
          <div class="rental-item" data-rental-id="${rental.id}">
            ${showProgress ? `
              <div class="days-left">
                <span class="icon">↻</span>
                ${daysLeft > 0 ? `${daysLeft} Days Left` : 'Expired'}
              </div>
            ` : ''}
            
            <div class="rental-actions">
              ${actions.can_purchase ? 
                `<span class="purchase-product">Purchase at a discount</span>` : ''
              }
              ${actions.can_extend ? 
                `<span class="extend-tenure">Extend Tenure</span>` : ''
              }
              ${actions.can_return ? 
                `<span class="return-product">Return Product</span>` : ''
              }
            </div>
  
            <div class="product-info">
              <img src="${rental.product_image || 'https://placehold.co/120x120?text=Product+Image'}" 
                   alt="${rental.product_name}">
              <div class="product-details">
                <h3>${rental.product_name}</h3>
                <div class="rental-details">
                  <div class="detail-item">
                    <span>Rented for:</span>
                    <strong>${rental.rental_period} Months</strong>
                  </div>
                  <div class="detail-item">
                    <span>Subscription Start:</span>
                    <strong>${isDelivered ? startDate.toLocaleDateString('en-IN') : 'Pending Delivery'}</strong>
                  </div>
                  <div class="detail-item">
                    <span>Subscription End:</span>
                    <strong>${endDate.toLocaleDateString('en-IN')}</strong>
                  </div>
                  <div class="detail-item">
                    <span>Order Status:</span>
                    ${statusHtml}
                  </div>
                </div>
              </div>
            </div>
  
            ${showProgress ? `
              <div style="margin: 15px 0; padding: 0;">
                <label style="display: block; margin-bottom: 8px;">Rental Progress</label>
                <div style="display: flex; align-items: center; gap: 10px; width: 100%;">
                  <div style="
                    flex: 1;
                    height: 8px;
                    background-color: #f0f0f0;
                    border-radius: 4px;
                    position: relative;
                    overflow: hidden;
                    display: block;
                  ">
                    <div style="
                      width: ${progress}%;
                      height: 8px;
                      background-color: #FF4444;
                      position: absolute;
                      left: 0;
                      top: 0;
                      display: block;
                    "></div>
                  </div>
                  <div style="min-width: 120px;">${daysLeft} days remaining</div>
                </div>
              </div>
            ` : ''}
  
            <button class="go-to-order" onclick="window.location.href='/pages/rental-order?order_number=${rental.order_number}&email=${encodeURIComponent(this.customerEmail)}'">Go to Order</button>
          </div>
        `;
      }).join('');
    }
  }
  
  window.rentalDashboard = new RentalDashboard();